import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChatTeardrop, BookOpen, BookmarkSimple, Compass, PencilSimpleLine } from '@phosphor-icons/react';
import { useChatStore } from '../../store/chatStore';
import { Logo } from './Logo';
import { useAuth } from '../../contexts/AuthContext';
import { Tooltip } from '../ui/Tooltip';
import { ChatList } from '../chat/ChatList';
import fullLogo from '../../../assets/images/theos-logo-full.svg';

export function Sidebar() {
  const { 
    isDrawerOpen, 
    setDrawerOpen, 
    chats, 
    deleteChat,
    setShareDialogOpen,
    setCurrentChat
  } = useChatStore();
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { path: '/', label: 'Chat', icon: ChatTeardrop, matchPath: (path: string) => path === '/' || path.startsWith('/chat/') },
    { path: '/explore', label: 'Explore', icon: Compass, matchPath: (path: string) => path.startsWith('/explore') },
    { path: '/bible', label: 'Study', icon: BookOpen, matchPath: (path: string) => path.startsWith('/bible') },
    { path: '/bookmarks', label: 'Bookmarks', icon: BookmarkSimple, matchPath: (path: string) => path.startsWith('/bookmarks') }
  ];

  const handleNewChat = () => {
    navigate('/?focus=true');
    setDrawerOpen(false);
  };

  const handleChatClick = (chatId: string) => {
    navigate(`/chat/${chatId}`);
    setDrawerOpen(false);
  };

  const handleShareChat = async (chatId: string) => {
    const chat = chats.find(c => c.id === chatId);
    if (chat) {
      setCurrentChat(chat);
      setShareDialogOpen(true);
    }
  };

  const handleDeleteChat = async (chatId: string) => {
    try {
      await deleteChat(chatId);
      if (location.pathname === `/chat/${chatId}`) {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
    }
  };

  const handleMouseLeave = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      {/* Mobile Backdrop */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 md:hidden
          ${isDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setDrawerOpen(false)}
        style={{ zIndex: 60 }}
      />

      {/* Sidebar */}
      <div
        className={`z-50 fixed top-0 left-0 h-full bg-[#F9F7F4] transition-all duration-300
          w-[280px] ${isDrawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ zIndex: 70 }}
        onMouseLeave={handleMouseLeave}
      >
        <div className="h-full flex flex-col w-full">
          {/* Header */}
          <div className="flex-none">
            <div className="h-16 flex items-center justify-between px-4">
              <div 
                className="cursor-pointer" 
                onClick={() => navigate('/')}
              >
                <img 
                  src={fullLogo}
                  alt="Theos" 
                  className="h-[30px]"
                />
              </div>
              <Tooltip content="New chat">
                <button
                  onClick={handleNewChat}
                  className="p-1 rounded-lg hover:bg-[rgba(0,0,0,0.05)]"
                >
                  <PencilSimpleLine className="w-6 h-6 text-[#361E06]" weight="bold" />
                </button>
              </Tooltip>
            </div>

            <nav className="px-2 mb-6 mt-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setDrawerOpen(false)}
                  className={`flex items-center h-10 px-3 rounded-lg transition-colors
                    ${item.matchPath(location.pathname) ? 'bg-[rgba(0,0,0,0.05)]' : 'hover:bg-[rgba(0,0,0,0.05)]'}`}
                >
                  <div className="w-6 flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-[#361E06]" weight="fill" />
                  </div>
                  <span className="ml-3 text-[#361E06] font-medium">{item.label}</span>
                </Link>
              ))}
            </nav>
          </div>

          {/* Chat List */}
          <div className="flex-grow overflow-y-auto">
            <div className="px-2">
              <ChatList 
                chats={chats} 
                onChatClick={handleChatClick}
                onShareChat={handleShareChat}
                onDeleteChat={handleDeleteChat}
                activeChatId={location.pathname.startsWith('/chat/') ? location.pathname.split('/')[2] : undefined}
                hasMore={false}
                isLoading={false}
                onLoadMore={() => {}}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}